import React, { useState, useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const FAQ = () => {
    // State to track which FAQ item is open
    const [openFAQ, setOpenFAQ] = useState(null);
    const answerRefs = useRef([]);
    const iconRefs = useRef([]);
    const headingRef = useRef(null);
    const descriptionRef = useRef(null);
    const sectionRef = useRef(null);

    // Initialize refs array and animations
    useEffect(() => {
        answerRefs.current = answerRefs.current.slice(0, faqItems.length);
        iconRefs.current = iconRefs.current.slice(0, faqItems.length);

        const ctx = gsap.context(() => {
            if (headingRef.current && descriptionRef.current) {
                gsap.fromTo(
                    headingRef.current,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.8,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: headingRef.current,
                            start: "top bottom-=100",
                            toggleActions: "play none none none"
                        }
                    }
                );

                gsap.fromTo(
                    descriptionRef.current,
                    { opacity: 0, y: 20 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.8,
                        delay: 0.2,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: descriptionRef.current,
                            start: "top bottom-=80",
                            toggleActions: "play none none none"
                        }
                    }
                );
            }
        }, sectionRef);

        return () => {
            ctx.revert();
        };
    }, []);

    // Toggle FAQ item with animation
    const toggleFAQ = (index) => {
        // If we're closing the current open FAQ
        if (openFAQ === index) {
            // Animate closing
            const answerElement = answerRefs.current[index];
            const iconElement = iconRefs.current[index];
            
            gsap.to(answerElement, {
                height: 0,
                opacity: 0,
                duration: 0.3,
                ease: "power2.out",
                onComplete: () => setOpenFAQ(null)
            });
            
            gsap.to(iconElement, {
                rotation: 0,
                duration: 0.3,
                ease: "back.out"
            });
        } 
        // If we're opening a new FAQ
        else {
            // Close the currently open FAQ if there is one
            if (openFAQ !== null) {
                const currentAnswerElement = answerRefs.current[openFAQ];
                const currentIconElement = iconRefs.current[openFAQ];
                
                gsap.to(currentAnswerElement, {
                    height: 0,
                    opacity: 0,
                    duration: 0.3,
                    ease: "power2.out"
                });
                
                gsap.to(currentIconElement, {
                    rotation: 0,
                    duration: 0.3,
                    ease: "back.out"
                });
            }
            
            // Set the new open FAQ
            setOpenFAQ(index);
            
            // We need to wait for the next render cycle when the element is available
            setTimeout(() => {
                const answerElement = answerRefs.current[index];
                const iconElement = iconRefs.current[index];
                
                if (answerElement && iconElement) {
                    // Get the natural height of the content
                    const height = answerElement.scrollHeight;
                    
                    // Animate opening
                    gsap.fromTo(answerElement, 
                        { height: 0, opacity: 0 },
                        { height: height, opacity: 1, duration: 0.4, ease: "power2.out" }
                    );
                    
                    gsap.to(iconElement, {
                        rotation: 135,
                        duration: 0.3,
                        ease: "back.out"
                    });
                }
            }, 10);
        }
    };

    // FAQ data
    const faqItems = [
        {
            question: "How does Agastya ensure the quality of its products?",
            answer: "Agastya follows strict manufacturing standards with 10+ quality checks on every product batch. From sourcing raw materials to final packaging, we maintain precision and consistency to deliver only the most effective and reliable solutions to farmers."
        },
        {
            question: "How can I become a retailer or distributor for Agastya products?",
            answer: "To become a retailer or distributor for Agastya products, please reach out to our business development team through our contact page or email us at partnerships@agastya.com. We'll guide you through our partnership process."
        },
        {
            question: "Are your products safe for long-term soil health?",
            answer: "Yes, our products are designed with soil health in mind. We formulate our agricultural solutions to promote sustainable farming practices and ensure long-term soil fertility and health."
        },
        {
            question: "What types of agricultural products does Agastya Agro offer?",
            answer: "Agastya Agro offers a comprehensive range of agricultural products including fertilizers, pesticides, growth enhancers, soil conditioners, and specialized crop-specific solutions."
        },
        {
            question: "Where can I buy Agastya Agro products?",
            answer: "Agastya Agro products are available through our authorized retail network across the country. You can locate your nearest retailer using our 'Find a Retailer' feature on our website or contact our customer service team."
        },
        {
            question: "Does Agastya offer any guidance on product usage?",
            answer: "Yes, we provide detailed usage instructions with all our products. Additionally, our technical team offers guidance through our website, instructional videos, and direct consultations for optimal product application."
        }
    ];

    return (
        <div ref={sectionRef} className="py-12">
            <div className="w-full max-w-[1280px] mx-auto px-4">
                <div className="flex flex-col md:flex-row gap-[40px] lg:gap-[138px] md:gap-[20px] place-content-between justify-center">
                    {/* FAQ Title and Description */}
                    <div className="w-full max-w-[400px] text-center md:text-left mx-auto">
                        <h2 ref={headingRef} className="text-[28px] md:text-[44px] font-medium mb-4 font-[LTWave] px-4">
                            Frequently Asked Questions
                        </h2>
                        <p ref={descriptionRef} className="tex-[16px] md:text-lg font-inter font-regular text-gray-700 px-4 opacity-50">
                            For any unanswered questions, reach out to our support team via email. 
                            We'll respond as soon as possible to assist you
                        </p>
                    </div>

                    {/* FAQ Items */}
                    <div className="w-full max-w-[730px]">
                        {faqItems.map((item, index) => (
                            <div key={index} className={`${index === 0 ? 'pb-4' : 'py-[20px] md:py-4'}`}>
                                <button
                                    className="flex justify-between items-center w-full text-left gap-[20px]"
                                    onClick={() => toggleFAQ(index)}
                                    aria-expanded={openFAQ === index}
                                >
                                    <h3 className="font-medium font-[LTWave] font-[20px]">{item.question}</h3>
                                    <span 
                                        ref={el => iconRefs.current[index] = el}
                                        className="text-2xl font-medium font-[LTWave] transition-transform duration-300"
                                        style={{ display: 'inline-block' }}
                                    >
                                        +
                                    </span>
                                </button>
                                <div 
                                    ref={el => answerRefs.current[index] = el}
                                    className="overflow-hidden"
                                    style={{ 
                                        height: openFAQ === index ? 'auto' : 0,
                                        opacity: openFAQ === index ? 1 : 0,
                                    }}
                                >
                                    <div className="mt-2 py-2">
                                        <p className="font-regular font-inter font-[20px] opacity-50">{item.answer}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FAQ;
